<template>
  <!-- The 3-step "build your order" wizard, mirroring the real desktop Search
       page (components/Views/Search/Index.vue): ① Choose Company → ② Find Your
       Machine → ③ Find Your Part (assembly dropdown + "show wear parts only"
       toggle + parts grouped by sub-assembly). Shared by the "Build your order"
       card and the Search Parts page. Adds to the shared mock cart (`sim`).
       Placeholder data only. -->
  <div class="cwz">
    <!-- Live-vs-sample indicator. The preview runs in the admin's logged-in
         session, so it calls the SAME authenticated API the real Search page
         uses (reads only — adds stay simulated). Falls back to sample data if
         the live API is unavailable. -->
    <div v-if="dataMode === 'real'" class="cwz-databar is-live">● Live data</div>
    <div v-else-if="dataMode === 'sample'" class="cwz-databar is-sample">Sample data — live API unavailable in preview</div>
    <div v-else class="cwz-databar is-loading">Loading…</div>

    <div class="cwz-steps">
      <button v-for="n in 3" :key="n" class="cwz-dot"
              :class="{ done: stepDone(n), active: step === n }"
              @click.stop="step = n">{{ n }}</button>
    </div>

    <!-- Step 1: Choose Company — searchable dropdown (type to narrow the list) -->
    <div v-if="step === 1" class="cwz-step">
      <div class="cwz-h"><span class="cwz-num" :class="{ ok: !!company }">1</span> Choose Company</div>
      <div v-if="loadingCompanies" class="cwz-note">Loading companies…</div>
      <div v-else-if="companies.length" class="cwz-combo" :class="{ open: companyOpen }">
        <button type="button" class="cwz-combo-field" @click.stop="toggleCompanyOpen">
          <span :class="{ 'cwz-combo-ph': !company }">{{ companyFieldLabel }}</span>
          <span class="cwz-combo-caret">▾</span>
        </button>
        <div v-if="companyOpen" class="cwz-combo-menu" @click.stop>
          <input ref="companySearch" class="cwz-combo-search" type="text"
                 v-model="companyQuery" placeholder="Search companies…" />
          <ul class="cwz-combo-list">
            <li v-for="c in filteredCompanies" :key="c.id" :class="{ sel: company === c.id }"
                @click="pickCompany(c)">{{ c.name }} <span v-if="c.code">({{ c.code }})</span></li>
            <li v-if="!filteredCompanies.length" class="cwz-combo-empty">No matches</li>
          </ul>
        </div>
      </div>
      <div v-if="!loadingCompanies && !companies.length" class="cwz-note">No companies available.</div>
    </div>

    <!-- Step 2: Find Your Machine -->
    <div v-else-if="step === 2" class="cwz-step">
      <div class="cwz-h"><span class="cwz-num" :class="{ ok: !!machine }">2</span> Find Your Machine</div>
      <div v-if="companyName" class="cwz-note">Company: {{ companyName }}</div>
      <label class="cwz-lbl">Choose a Machine</label>
      <div v-if="loadingMachines" class="cwz-note">Loading machines…</div>
      <div v-else-if="machines.length" class="cwz-combo" :class="{ open: machineOpen }">
        <button type="button" class="cwz-combo-field" @click.stop="toggleMachineOpen">
          <span :class="{ 'cwz-combo-ph': !machine }">{{ machineFieldLabel }}</span>
          <span class="cwz-combo-caret">▾</span>
        </button>
        <div v-if="machineOpen" class="cwz-combo-menu" @click.stop>
          <input ref="machineSearch" class="cwz-combo-search" type="text"
                 v-model="machineQuery" placeholder="Search machines…" />
          <ul class="cwz-combo-list">
            <li v-for="m in filteredMachines" :key="m.id" :class="{ sel: machine === m.id }"
                @click="pickMachine(m)">{{ m.name }}</li>
            <li v-if="!filteredMachines.length" class="cwz-combo-empty">No matches</li>
          </ul>
        </div>
      </div>
      <div v-if="loadErr" class="cwz-err-row">
        <span class="cwz-note cwz-err">{{ loadErr }}</span>
        <button type="button" class="cwz-retry" @click.stop="retryLoad">Retry</button>
      </div>
    </div>

    <!-- Step 3: Find Your Part — assembly dropdown + wear toggle + parts by sub-assembly -->
    <div v-else class="cwz-step">
      <div class="cwz-h3">
        <span class="cwz-h"><span class="cwz-num" :class="{ ok: !!assemblyId }">3</span> Choose Your Assembly</span>
        <label class="cwz-check">
          <input type="checkbox" v-model="wearOnly" />
          <span>Show wear parts only <small>uncheck to browse every part</small></span>
        </label>
      </div>
      <div v-if="machine" class="cwz-note">{{ companyName }} · {{ machineName }}</div>

      <label class="cwz-lbl">Choose an Assembly</label>
      <div v-if="loadingAssemblies" class="cwz-note">Loading assemblies…</div>
      <select class="cwz-select" v-model="assemblyId" @change="onAssemblyChange">
        <option value="">— Select an assembly —</option>
        <option v-for="a in assemblies" :key="a.id" :value="a.id">{{ a.name }}</option>
      </select>
      <div v-if="loadErr" class="cwz-err-row">
        <span class="cwz-note cwz-err">{{ loadErr }}</span>
        <button type="button" class="cwz-retry" @click.stop="retryLoad">Retry</button>
      </div>

      <template v-if="assemblyId">
        <div v-if="loadingParts" class="cwz-note">Loading parts…</div>
        <div v-for="g in groupedParts" :key="g.sub" class="cwz-subgroup">
          <div class="cwz-sub-h">{{ g.sub }}</div>
          <ul class="cwz-parts">
            <li v-for="p in g.parts" :key="p.sku" @click.stop="pick(p)">
              <span class="cwz-pname">
                <b>{{ p.sku }}</b> — {{ p.name }}
                <span v-if="p.wear" class="cwz-wear">wear</span>
              </span>
              <span class="cwz-price">${{ p.price.toFixed(2) }}</span>
              <em class="cwz-add">+ Add</em>
            </li>
          </ul>
        </div>
        <div v-if="!loadingParts && !groupedParts.length" class="cwz-note">No {{ wearOnly ? 'wear ' : '' }}parts in this assembly.</div>
      </template>
      <div v-else class="cwz-note">Select an assembly to see its parts.</div>

      <!-- Cart lives in the floating Cart part, not in the build card. -->
    </div>

    <!-- Add modal — Order (PO Number) or Quote (Reference), like /search -->
    <div v-if="modalPart" class="cwz-modal" @click.self="modalPart = null">
      <div class="cwz-modal-card">
        <div class="cwz-modal-head">Add to {{ dest === 'quote' ? 'Quote' : 'Order' }}</div>
        <div class="cwz-modal-part">{{ modalPart.sku }} — {{ modalPart.name }}</div>
        <div class="cwz-dest">
          <button :class="{ on: dest === 'order' }" @click.stop="dest = 'order'">Order</button>
          <button :class="{ on: dest === 'quote' }" @click.stop="dest = 'quote'">Quote</button>
        </div>
        <label class="cwz-lbl">{{ dest === 'quote' ? 'Quote Reference' : 'PO Number' }}</label>
        <input class="cwz-select" v-model="modalRef"
               :placeholder="dest === 'quote' ? 'Enter Quote Reference' : 'Enter PO Number'" />
        <label class="cwz-lbl">Quantity</label>
        <input class="cwz-select" type="number" min="1" v-model="modalQty" />
        <div class="cwz-modal-actions">
          <button class="cwz-modal-cancel" @click.stop="modalPart = null">Cancel</button>
          <button class="cwz-modal-ok" @click.stop="confirmAdd">Add to {{ dest === 'quote' ? 'Quote' : 'Order' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FeApiService from "@/assets/js/FeApiService";

// One shared service instance — mirrors the real Search page
// (components/Views/Search/Index.vue), which instantiates FeApiService at
// module scope. It uses the same authenticated httpClient (Bearer token from
// localStorage), so the composer preview reads the SAME live data the
// published site shows. Writes (add-to-cart) stay simulated — see confirmAdd.
const apiService = new FeApiService();

export default {
  name: "ComposerSearchWizard",
  props: {
    sim: {
      type: Object,
      default: function () {
        return { cart: [] };
      },
    },
  },
  data() {
    return {
      step: 1,
      company: "", // selected company id (the select-item value)
      companyName: "",
      machine: "", // selected companyMachineId (the select-item value)
      assemblyId: "", // selected assembly id (the select-item value)
      placed: false,
      wearOnly: true, // matches the portal default — wear parts only
      modalPart: null, // the part being added (opens the Order/Quote modal)
      dest: "order",
      modalRef: "",
      modalQty: 1,

      // Step 1 searchable company dropdown.
      companyOpen: false,
      companyQuery: "",
      // Step 2 searchable machine dropdown.
      machineOpen: false,
      machineQuery: "",

      // "loading" until the first company fetch resolves, then "real" if the
      // live API answered or "sample" if it failed / returned nothing.
      dataMode: "loading",
      loadingCompanies: false,
      loadingMachines: false,
      loadingAssemblies: false,
      loadingParts: false,
      loadErr: "",

      // Active dropdown sources — filled from the API in real mode, or from the
      // sample* arrays below in sample mode. Always [{ id, name }] (companies
      // also carry an optional display `code`).
      companies: [],
      machines: [],
      assemblies: [],
      // Real parts grouped by sub-assembly:
      // [{ sub, parts: [{ sku, name, price, wear, sub }] }].
      realGroups: [],


      // ---- Sample fallback data (used only when the live API is unavailable) ----
      sampleCompanies: [
        { id: "NC999", name: "JC Steele & Sons", code: "NC999" },
        { id: "TX102", name: "Acme Brick", code: "TX102" },
        { id: "OH410", name: "Belden Brick", code: "OH410" },
      ],
      sampleMachines: [
        { id: "90ADEX Extruder", name: "90ADEX Extruder" },
        { id: "75 Pug Sealer", name: "75 Pug Sealer" },
        { id: "90A Pug Mill", name: "90A Pug Mill" },
        { id: "Model IIA Mixer", name: "Model IIA Mixer" },
      ],
      // Each sample assembly holds parts; each part has a sub-assembly + wear flag.
      sampleAssemblies: [
        {
          id: "EXT",
          name: "Extruder Assembly",
          parts: [
            { sku: "DSD S 0488 SR", name: "Spiral groove liner — R", sub: "Liners", wear: true, price: 312.5 },
            { sku: "DSD S 0486 SR", name: "Spiral groove liner — L", sub: "Liners", wear: true, price: 312.5 },
            { sku: "DBE S 0130 CI", name: "Die backing", sub: "Die", wear: false, price: 757.4 },
            { sku: "SHF S 0090 SS", name: "Extruder shaft", sub: "Drive", wear: false, price: 998.0 },
          ],
        },
        {
          id: "PUG",
          name: "Pug Sealer Assembly",
          parts: [
            { sku: "NDS S 2011 PC", name: "Pug sealer liner", sub: "Liners", wear: true, price: 489.5 },
            { sku: "PSA S 3407 SP", name: "Pug sealer paddle", sub: "Paddles", wear: true, price: 196.04 },
            { sku: "NDH S 2011 PC", name: "Knife holder", sub: "Knives", wear: false, price: 264.0 },
          ],
        },
        {
          id: "MIX",
          name: "Mixer Assembly",
          parts: [
            { sku: "BLD S 2204 CI", name: "Mixer blade", sub: "Blades", wear: true, price: 178.9 },
            { sku: "NDS S 1180 PC", name: "Auger flight", sub: "Auger", wear: true, price: 612.25 },
          ],
        },
      ],
    };
  },
  computed: {
    machineName() {
      for (var i = 0; i < this.machines.length; i++) {
        if (String(this.machines[i].id) === String(this.machine)) return this.machines[i].name;
      }
      return "";
    },
    // Label shown on the closed company dropdown field.
    companyFieldLabel() {
      if (!this.company) return "— Select a company —";
      for (var i = 0; i < this.companies.length; i++) {
        var c = this.companies[i];
        if (c.id === this.company) {
          return c.code ? c.name + " (" + c.code + ")" : c.name;
        }
      }
      return this.companyName || "— Select a company —";
    },
    // Companies filtered by the search box (matches name or code).
    filteredCompanies() {
      var q = (this.companyQuery || "").trim().toLowerCase();
      if (!q) return this.companies;
      return this.companies.filter(function (c) {
        return (
          (c.name && c.name.toLowerCase().indexOf(q) >= 0) ||
          (c.code && String(c.code).toLowerCase().indexOf(q) >= 0)
        );
      });
    },
    // Label shown on the closed machine dropdown field.
    machineFieldLabel() {
      return this.machine ? this.machineName || "Selected machine" : "— Select a machine —";
    },
    // Machines filtered by the search box (matches name).
    filteredMachines() {
      var q = (this.machineQuery || "").trim().toLowerCase();
      if (!q) return this.machines;
      return this.machines.filter(function (m) {
        return m.name && m.name.toLowerCase().indexOf(q) >= 0;
      });
    },
    // Parts of the chosen assembly, grouped by sub-assembly. In real mode the
    // API already returns them grouped + wear-filtered (the toggle picks the
    // wear vs all-parts endpoint); in sample mode we group/filter the local
    // sampleAssemblies client-side.
    groupedParts() {
      if (this.dataMode === "real") return this.realGroups;
      var asm = null;
      for (var a = 0; a < this.sampleAssemblies.length; a++) {
        if (this.sampleAssemblies[a].id === this.assemblyId) {
          asm = this.sampleAssemblies[a];
          break;
        }
      }
      if (!asm) return [];
      var groups = {};
      var order = [];
      for (var i = 0; i < asm.parts.length; i++) {
        var p = asm.parts[i];
        if (this.wearOnly && !p.wear) continue;
        if (!groups[p.sub]) {
          groups[p.sub] = [];
          order.push(p.sub);
        }
        groups[p.sub].push(p);
      }
      return order.map(function (s) {
        return { sub: s, parts: groups[s] };
      });
    },
  },
  watch: {
    // Toggling wear-only swaps which endpoint feeds the parts list (real mode);
    // sample mode just re-filters via the computed.
    wearOnly() {
      if (this.dataMode === "real" && this.assemblyId) this.loadParts();
    },
    // Collapse any open combo when moving between steps.
    step() {
      this.companyOpen = false;
      this.machineOpen = false;
    },
  },
  mounted() {
    this.loadCompanies();
    document.addEventListener("click", this.closeCombos);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeCombos);
  },
  methods: {
    // --- Real-data loaders (reads only) -----------------------------------
    async loadCompanies() {
      this.loadingCompanies = true;
      this.loadErr = "";
      try {
        var data = await this._race(apiService.getCompanyList(), "companies");
        var opts = this.normalizeSelect(data);
        if (opts.length) {
          this.companies = opts;
          this.dataMode = "real";
        } else {
          // Empty/odd response — fall back to sample so the canvas isn't blank.
          this.useSample();
        }
      } catch (e) {
        // Companies are the entry point; if they fail, sample mode keeps the
        // whole wizard usable (machines/assemblies/parts would have no real
        // ids to chain anyway). Log for diagnosis.
        try {
          console.error("[ComposerWizard] companies load failed — using sample", e);
        } catch (x) {}
        this.useSample();
      } finally {
        this.loadingCompanies = false;
      }
    },
    async loadMachines() {
      if (this.dataMode === "sample") {
        this.machines = this.sampleMachines.slice();
        return;
      }
      this.loadingMachines = true;
      this.loadErr = "";
      try {
        var data = await this._race(
          apiService.getMachinesByCompany({ id: this.company }),
          "machines"
        );
        this.machines = this.normalizeSelect(data);
        if (!this.machines.length) this.loadErr = "No machines found for this company.";
      } catch (e) {
        this.machines = [];
        this.loadErr = this._apiErr("machines", e);
      } finally {
        // ALWAYS clear the spinner. The previous seq-gated reset is what left
        // "Loading machines…" wedged even though the request returned 200.
        this.loadingMachines = false;
      }
    },
    async loadAssemblies() {
      if (this.dataMode === "sample") {
        this.assemblies = this.sampleAssemblies.map(function (a) {
          return { id: a.id, name: a.name };
        });
        return;
      }
      this.loadingAssemblies = true;
      this.loadErr = "";
      try {
        var data = await this._race(
          apiService.getSelectItemsByCompanyMachine({ id: this.machine }),
          "assemblies"
        );
        this.assemblies = this.normalizeSelect(data);
        if (!this.assemblies.length) this.loadErr = "No assemblies found for this machine.";
      } catch (e) {
        this.assemblies = [];
        this.loadErr = this._apiErr("assemblies", e);
      } finally {
        this.loadingAssemblies = false;
      }
    },
    async loadParts() {
      this.realGroups = [];
      if (this.dataMode === "sample" || !this.assemblyId) return;
      this.loadingParts = true;
      this.loadErr = "";
      var wearMode = this.wearOnly;
      try {
        var data;
        if (wearMode) {
          // Wear-parts only — same endpoint the real Search page hits.
          data = await this._race(
            apiService.getAssembliesWithWearParts({
              assemblyId: this.assemblyId,
              companyMachineId: this.machine,
            }),
            "parts"
          );
        } else {
          // Every part on the assembly. Pass the chosen company so the
          // backend hides per-company-deactivated parts.
          data = await this._race(
            apiService.getAssemblyWithParts({
              id: this.assemblyId,
              companyId: this.company,
            }),
            "parts"
          );
        }
        this.realGroups = this.normalizeParts(data, wearMode);
      } catch (e) {
        this.realGroups = [];
        this.loadErr = this._apiErr("parts", e);
      } finally {
        this.loadingParts = false;
      }
    },

    // --- Response normalizers ---------------------------------------------
    // Backend select endpoints return { value, text }; tolerate id/name variants.
    normalizeSelect(data) {
      var rows = Array.isArray(data) ? data : [];
      return rows.map(function (r) {
        var id = r.value != null ? r.value : (r.id != null ? r.id : r.key);
        var name = r.text || r.name || r.label || "";
        return { id: id == null ? "" : String(id), name: name };
      }).filter(function (o) {
        return o.id && o.name;
      });
    },
    // Both the wear and all-parts endpoints return sub-assemblies grouped:
    // [{ assemblyName, parts: [{ item, code, description, price }] }]. Map to
    // the wizard's display shape. In wear mode every returned part is a wear
    // part; in all-parts mode only flag those the backend marks.
    normalizeParts(data, wearMode) {
      var rows = Array.isArray(data) ? data : [];
      return rows.map(function (g, gi) {
        var sub = g.assemblyName || g.text || g.name || ("Sub-assembly " + (gi + 1));
        var parts = (g.parts || []).map(function (p) {
          var price = p.price != null ? p.price : (p.unitPrice != null ? p.unitPrice : 0);
          return {
            sku: p.item || p.code || p.partNumber || p.sku || "",
            name: p.description || p.name || "",
            price: Number(price) || 0,
            wear: wearMode ? true : !!(p.isWearPart || p.wear || p.isWear),
            sub: sub,
          };
        }).filter(function (p) {
          return p.sku || p.name;
        });
        return { sub: sub, parts: parts };
      }).filter(function (g) {
        return g.parts.length;
      });
    },
    useSample() {
      this.dataMode = "sample";
      this.companies = this.sampleCompanies.slice();
    },
    // Reject a slow request after a ceiling so the preview never hangs on an
    // infinite spinner — the real Search page can't get stuck because it lives
    // on its own page, but here a wedged request would freeze the wizard.
    _race(promise, label) {
      var ms = 12000;
      var timer;
      var timeout = new Promise(function (_, reject) {
        timer = setTimeout(function () {
          reject(new Error(label + " request timed out (" + ms / 1000 + "s)"));
        }, ms);
      });
      return Promise.race([Promise.resolve(promise), timeout]).finally(function () {
        clearTimeout(timer);
      });
    },
    // Screenshot-friendly error string (shown in the wizard) + console detail.
    _apiErr(label, e) {
      try {
        console.error("[ComposerWizard] " + label + " load failed", e);
      } catch (x) {}
      var detail =
        (e && e.response && e.response.status
          ? "HTTP " + e.response.status
          : e && e.message) || e;
      return "Couldn't load " + label + ": " + detail;
    },
    // Retry whichever load matches the current step (used by the error Retry button).
    retryLoad() {
      if (this.step === 2) return this.loadMachines();
      if (this.step === 3) {
        if (!this.assemblies.length) return this.loadAssemblies();
        return this.loadParts();
      }
      return this.loadCompanies();
    },

    // --- Searchable dropdowns (Step 1 company, Step 2 machine) -------------
    toggleCompanyOpen() {
      this.companyOpen = !this.companyOpen;
      if (this.companyOpen) {
        this.companyQuery = "";
        this.focusRef("companySearch");
      }
    },
    toggleMachineOpen() {
      this.machineOpen = !this.machineOpen;
      if (this.machineOpen) {
        this.machineQuery = "";
        this.focusRef("machineSearch");
      }
    },
    focusRef(name) {
      var self = this;
      this.$nextTick(function () {
        try {
          if (self.$refs[name]) self.$refs[name].focus();
        } catch (e) {}
      });
    },
    // Document-level click handler (registered in mounted) — closes any open
    // dropdown when the user clicks outside it. Clicks on a field/menu stop
    // propagation, so this only fires for true outside clicks.
    closeCombos() {
      this.companyOpen = false;
      this.machineOpen = false;
    },

    // --- Step navigation ---------------------------------------------------
    pickCompany(c) {
      this.company = c.id;
      this.companyName = c.name;
      this.companyOpen = false;
      this.companyQuery = "";
      this.step = 2;
      this.machine = "";
      this.assemblyId = "";
      this.machines = [];
      this.assemblies = [];
      this.realGroups = [];
      this.loadMachines();
    },
    pickMachine(m) {
      this.machine = m.id;
      this.machineOpen = false;
      this.machineQuery = "";
      this.step = 3;
      this.assemblyId = "";
      this.assemblies = [];
      this.realGroups = [];
      this.loadAssemblies();
    },
    onAssemblyChange() {
      if (this.assemblyId) this.loadParts();
      else this.realGroups = [];
    },
    stepDone(n) {
      if (n === 1) return !!this.company;
      if (n === 2) return !!this.machine;
      return !!this.assemblyId;
    },

    // --- Add to cart (SIMULATED — never writes to the real cart) -----------
    pick(p) {
      // Open the add modal — choose Order (PO Number) or Quote (Reference),
      // then quantity — mirroring the real /search add-to-cart flow.
      this.modalPart = p;
      this.dest = "order";
      this.modalRef = "";
      this.modalQty = 1;
    },
    confirmAdd() {
      var p = this.modalPart;
      if (!p) return;
      if (!this.sim.cart) this.sim.cart = [];
      var asmName = "";
      for (var i = 0; i < this.assemblies.length; i++) {
        if (String(this.assemblies[i].id) === String(this.assemblyId)) {
          asmName = this.assemblies[i].name;
          break;
        }
      }
      this.sim.cart.push({
        sku: p.sku,
        name: p.name,
        price: p.price || 0,
        qty: Math.max(1, parseInt(this.modalQty, 10) || 1),
        dest: this.dest === "quote" ? "Quote" : "Order",
        ref: (this.modalRef || "").trim(),
        company: this.companyName || this.company,
        machine: this.machineName,
        assembly: asmName,
        sub: p.sub || "",
        _open: false,
      });
      this.modalPart = null;
      this.placed = false;
    },
  },
};
</script>

<style scoped>
.cwz {
  position: relative;
  font-family: var(--cmp-font, -apple-system, "Segoe UI", Roboto, Arial, sans-serif);
  color: var(--cmp-color, #1f2733);
  font-size: var(--cmp-fs, 13px);
}
.cwz-steps {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.cwz-dot {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid #cfd3d7;
  background: #fff;
  color: #888;
  font-weight: 700;
  cursor: pointer;
}
.cwz-dot.active {
  border-color: #c8302a;
  color: #c8302a;
}
.cwz-dot.done {
  background: #2a7d2a;
  border-color: #2a7d2a;
  color: #fff;
}
.cwz-h3 {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.cwz-h {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 8px;
}
.cwz-num {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #888;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
}
.cwz-num.ok {
  background: #2a7d2a;
}
.cwz-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  cursor: pointer;
}
.cwz-check small {
  display: block;
  color: #999;
  font-size: 10px;
}
.cwz-note {
  color: var(--cmp-color, #4a4f57);
  margin: 8px 0;
}
/* Inline load error + Retry */
.cwz-err-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 8px 0;
}
.cwz-err-row .cwz-note {
  margin: 0;
}
.cwz-retry {
  border: 1px solid #c8302a;
  background: #fff;
  color: #c8302a;
  border-radius: 5px;
  padding: 4px 12px;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
}
.cwz-retry:hover {
  background: #fbe9e8;
}
/* Live-vs-sample data indicator */
.cwz-databar {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 3px 8px;
  border-radius: 4px;
  margin-bottom: 8px;
}
.cwz-databar.is-live {
  background: #e7f6e7;
  color: #2a7d2a;
}
.cwz-databar.is-sample {
  background: #fff4e5;
  color: #9a6400;
}
.cwz-databar.is-loading {
  background: #eef1f4;
  color: #777;
}
.cwz-err {
  color: #c8302a;
  font-weight: 600;
}
.cwz-lbl {
  display: block;
  font-weight: 600;
  margin: 6px 0 4px;
}
.cwz-select {
  width: 100%;
  border: 1px solid #cfd3d7;
  border-radius: 5px;
  padding: 8px 9px;
  font-size: var(--cmp-fs, 13px);
  background: #fff;
  color: var(--cmp-color, #1f2733);
}
/* Searchable company + machine dropdown */
.cwz-combo {
  position: relative;
}
.cwz-combo-field {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid #cfd3d7;
  border-radius: 5px;
  padding: 8px 9px;
  font-size: var(--cmp-fs, 13px);
  background: #fff;
  color: var(--cmp-color, #1f2733);
  cursor: pointer;
  text-align: left;
}
.cwz-combo.open .cwz-combo-field {
  border-color: #c8302a;
}
.cwz-combo-ph {
  color: #8a9099;
}
.cwz-combo-caret {
  flex: 0 0 auto;
  color: #8a9099;
  font-size: 11px;
}
.cwz-combo-menu {
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #cfd3d7;
  border-radius: 6px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}
.cwz-combo-search {
  width: 100%;
  border: 0;
  border-bottom: 1px solid #eceef0;
  padding: 8px 10px;
  font-size: var(--cmp-fs, 13px);
  color: var(--cmp-color, #1f2733);
  outline: none;
}
.cwz-combo-list {
  list-style: none;
  margin: 0;
  padding: 4px;
  max-height: 200px;
  overflow-y: auto;
}
.cwz-combo-list li {
  padding: 8px 10px;
  border-radius: 5px;
  cursor: pointer;
  color: var(--cmp-color, #1f2733);
  font-size: var(--cmp-fs, 13px);
}
.cwz-combo-list li span {
  color: #888;
}
.cwz-combo-list li:hover {
  background: #fbe9e8;
}
.cwz-combo-list li.sel {
  background: #fbe9e8;
  font-weight: 600;
}
.cwz-combo-empty {
  color: #888 !important;
  cursor: default !important;
}
.cwz-combo-empty:hover {
  background: transparent !important;
}
/* The composer previews the LIGHT customer site. The admin console itself may
   be in dark/mid mode, whose global !important rules (darkmode.css /
   mid-mode.css) repaint these <input>/<li>/<select> controls dark — which is
   what made the dropdown values look "light" and beat the editor's color.
   Force the light-site look here; var(--cmp-color) keeps the editor on top. */
html.dark-mode .cwz-combo-field,
html.mid-mode .cwz-combo-field,
html.dark-mode .cwz-combo-search,
html.mid-mode .cwz-combo-search,
html.dark-mode .cwz-combo-list li,
html.mid-mode .cwz-combo-list li,
html.dark-mode .cwz-select,
html.mid-mode .cwz-select {
  background: #fff !important;
  color: var(--cmp-color, #1f2733) !important;
}
html.dark-mode .cwz-combo-menu,
html.mid-mode .cwz-combo-menu {
  background: #fff !important;
}
html.dark-mode .cwz-combo-ph,
html.mid-mode .cwz-combo-ph,
html.dark-mode .cwz-combo-caret,
html.mid-mode .cwz-combo-caret {
  color: #8a9099 !important;
}
.cwz-pick {
  list-style: none;
  margin: 0;
  padding: 0;
}
.cwz-pick li {
  padding: 9px 11px;
  border: 1px solid #e2e5e8;
  border-radius: 6px;
  margin-bottom: 6px;
  cursor: pointer;
  color: #333;
}
.cwz-pick li span {
  color: #888;
}
.cwz-pick li:hover {
  background: #fbe9e8;
}
.cwz-pick li.sel {
  border-color: #c8302a;
  background: #fbe9e8;
  font-weight: 600;
}
.cwz-subgroup {
  margin-top: 10px;
}
.cwz-sub-h {
  font-weight: 700;
  color: var(--cmp-color, #2a2f36);
  background: #f1f3f5;
  border-left: 3px solid #c8302a;
  padding: 5px 8px;
  border-radius: 3px;
  font-size: 12px;
}
.cwz-parts {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
}
.cwz-parts li {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 6px;
  border-bottom: 1px solid #f1f1f1;
  cursor: pointer;
  font-size: 12px;
}
.cwz-parts li:hover {
  background: #fbe9e8;
}
.cwz-pname {
  flex: 1 1 auto;
  min-width: 0;
}
.cwz-wear {
  display: inline-block;
  margin-left: 4px;
  background: #e7f6e7;
  color: #2a7d2a;
  border-radius: 8px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}
.cwz-price {
  color: var(--cmp-color, #555);
  white-space: nowrap;
}
.cwz-add {
  color: #c8302a;
  font-weight: 700;
  font-style: normal;
  white-space: nowrap;
}
.cwz-sub {
  font-size: 13px;
  margin: 14px 0 6px;
}
.cwz-placed {
  margin-top: 8px;
  color: #2a7d2a;
  font-weight: 600;
  font-size: 12px;
}
.cwz-cart {
  list-style: none;
  margin: 0;
  padding: 0;
}
.cwz-cart li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #f1f1f1;
}
.cwz-citem {
  flex: 1 1 auto;
  min-width: 0;
}
.cwz-cartmeta {
  display: block;
  color: #888;
  font-size: 10px;
}
.cwz-qty {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.cwz-qty button {
  width: 22px;
  height: 22px;
  border: 1px solid #cfd3d7;
  background: #f6f7f8;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
}
.cwz-rm {
  border: 0;
  background: transparent;
  color: #c8302a;
  font-weight: 700;
  cursor: pointer;
}
.cwz-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  font-weight: 700;
}
.cwz-bar button {
  border: 0;
  border-radius: 6px;
  background: #c8302a;
  color: #fff;
  font-weight: 700;
  padding: 8px 14px;
  cursor: pointer;
}
/* Add-to-cart modal (Order / Quote) */
.cwz-modal {
  position: absolute;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 18px 10px;
  background: rgba(0, 0, 0, 0.45);
}
.cwz-modal-card {
  width: 100%;
  max-width: 320px;
  background: #fff;
  border-radius: 10px;
  padding: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}
.cwz-modal-head {
  font-weight: 700;
  font-size: 15px;
  color: #c8302a;
  margin-bottom: 4px;
}
.cwz-modal-part {
  font-size: 12px;
  color: #444;
  margin-bottom: 10px;
}
.cwz-dest {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}
.cwz-dest button {
  flex: 1 1 0;
  border: 1px solid #cfd3d7;
  background: #f6f7f8;
  border-radius: 6px;
  padding: 8px;
  font-weight: 600;
  color: #1d1d1d;
  cursor: pointer;
}
.cwz-dest button.on {
  border-color: #c8302a;
  background: #fbe9e8;
  color: #c8302a;
}
.cwz-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}
.cwz-modal-cancel {
  border: 1px solid #cfd3d7;
  background: #fff;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
}
.cwz-modal-ok {
  border: 0;
  background: #c8302a;
  color: #fff;
  border-radius: 6px;
  padding: 8px 14px;
  font-weight: 700;
  cursor: pointer;
}
</style>
